import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { settingsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardBody } from '@/components/ui/card';

export function MaintenanceSettingsPage() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [windowDays, setWindowDays] = useState('');
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
  });

  useEffect(() => {
    if (data) setWindowDays(String(data.pm_compliance_window_days ?? 0));
  }, [data]);

  const save = useMutation({
    mutationFn: () => settingsApi.update({ pm_compliance_window_days: parseInt(windowDays) || 0 }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-4 sm:px-6 py-3">
        <Link to="/schedules" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} />
          Back to schedules
        </Link>
      </div>

      <div className="max-w-xl mx-auto p-4 sm:p-6">
        <h1 className="text-xl font-bold text-slate-900 mb-1">Maintenance Settings</h1>
        <p className="text-sm text-slate-500 mb-6">Plant-wide defaults for preventive maintenance.</p>

        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <Card>
            <CardBody className="space-y-4">
              <div>
                <Input
                  label="PM compliance grace (days)"
                  type="number" min={0}
                  value={windowDays}
                  onChange={e => setWindowDays(e.target.value)}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-slate-500 mt-1.5">
                  A preventive maintenance task counts as on-time if it's completed within this many days
                  of its due date. Use <span className="font-medium">0</span> for a strict due date.
                  Individual tasks can override this.
                </p>
              </div>

              {isAdmin && (
                <div className="flex items-center gap-3 pt-1">
                  <Button onClick={() => save.mutate()} disabled={save.isPending}>
                    {save.isPending ? 'Saving…' : 'Save'}
                  </Button>
                  {saved && <span className="text-sm text-green-600">Saved</span>}
                  {save.error && <span className="text-sm text-red-600">{(save.error as Error).message}</span>}
                </div>
              )}
              {!isAdmin && (
                <p className="text-xs text-slate-400">Only an admin can change plant settings.</p>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}
